import { redirect } from 'react-router';
import { guestLoader, authLoader } from '@/navigation/loaders/authLoader';
import { lazyRoute } from '@/navigation/utils/lazyRoute';
import { RouteErrorBoundary } from '@/navigation/components/RouteErrorBoundary';

/**
 * Auth routes - for login, signup, password reset.
 * NO layout wrapper - minimal UI when coming from gatekeeper.
 * Redirects to dashboard if already logged in.
 *
 * Each route has its own errorElement to handle chunk load failures.
 * Loaders use `return redirect()` (not `throw`) to avoid error boundary flashes.
 */
export const authRoutes = [
  {
    path: '/login',
    loader: guestLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/login')),
  },
  // Step 1: Personal details
  {
    path: '/sign-up',
    loader: guestLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/signup')),
  },
  // Step 2: Password
  {
    path: '/sign-up/password',
    loader: guestLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/signup/password')),
  },
  // Step 3: Terms & conditions
  {
    path: '/sign-up/terms',
    loader: guestLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/accept-terms')),
  },
  // Redirect old /accept-terms to new path
  {
    path: '/accept-terms',
    loader: () => redirect('/sign-up/terms'),
  },
  // /lastlooks (the QR-code entry printed on physical marketing material)
  // used to live here as a redirect into /sign-up. It's now a real landing
  // page owned by publicRoutes so scanners get the site layout + footer.
  // Step 4: Profile photo (post-verification, authenticated, skippable)
  {
    path: '/sign-up/profile-pic',
    loader: authLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/signup/profile-pic')),
  },
  // Step 5: Social media (post-verification, authenticated), the last step
  {
    path: '/sign-up/socials',
    loader: authLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/signup/socials')),
  },
  {
    path: '/check-email',
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/check-email')),
  },
  {
    path: '/forgot-password',
    loader: guestLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/forgot-password')),
  },
  {
    path: '/reset-password',
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/reset-password')),
  },
  {
    path: '/verify-email',
    errorElement: <RouteErrorBoundary />,
    lazy: lazyRoute(() => import('@/component/auth/verify-email')),
  },
];
